import {Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {DatePipe, isPlatformBrowser, NgClass, NgForOf, NgIf} from '@angular/common';
import {PlaceService} from '../../../services/place.service';
import {Place} from '../../../models/place';
import {finalize, forkJoin, Subject, takeUntil} from 'rxjs';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {RegionService} from '../../../services/region-service';
import {Region} from '../../../models/region';
import moment from "moment";
import {User} from '../../../models/user';
import {AuthService} from '../../../services/auth.service';
import {ProductService} from '../../../services/product.service';
import {Countdown} from '../../../models/countdown';
import {CountdownService} from '../../../services/countdown.service';
import {LinkifyPipe} from '../../pipes/linkify.pipe';
import {MapMark, MapService} from '../../../services/map.service';
import {HoldService} from '../../../services/hold.service';
import {VisitService} from '../../../services/visit.service';
import {RouteService} from '../../../services/route.service';
import {environment} from '../../../environments/environment';
import {RoomService} from '../../../services/room-service';
import {Room} from '../../../models/room';
import {RoomBoxComponent} from '../../conference-room/room-box/room-box.component';
import {PlaceDetailHoursComponent} from './place-detail-hours/place-detail-hours.component';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import {SeoService} from '../../../services/seo.service';
import {ActivityService} from '../../../services/activity.service';
import {Activity} from '../../../models/activity';
import {round} from '@popperjs/core/lib/utils/math';

@Component({
  selector: 'app-place-detail',
  imports: [
    NgIf,
    NgClass,
    RouterLink,
    LinkifyPipe,
    DatePipe,
    NgForOf,
    RoomBoxComponent,
    PlaceDetailHoursComponent
  ],
  providers: [DatePipe],
  templateUrl: './place-detail.component.html',
  styleUrl: './place-detail.component.scss'
})
export class PlaceDetailComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  GOOGLE_MAP_API_KEY = environment.GOOGLE_MAP_API_KEY

  loading = false

  // Region
  regionSlug: string | null = null;
  region: Region | null = null;

  // Neighborhood
  neighborhoodSlug: string | null = null;

  // Place
  placeSlug: string | null = null;
  place: Place | null = null;

  // User
  currentUser: User | null = null;

  // Context navigation
  previousPath: string = "/discover"

  // Countdown
  countdown: Countdown = {
    productType: 'strawberry',
    visitRemaining: moment.duration(40, 'hours').asMilliseconds(),
    guestRemaining: 0,
    passRemaining: 0
  };
  remaining: string | null = null
  remainingUnit: string | null = null;

  // Rooms
  isRoomsLoading = false
  isRoomsLoaded = false
  rooms: Room[] = []

  // Gallery
  images: any[] = []
  gallery: any | null = null

  // Action
  isVisitSubmitted = false;
  isHoldSubmitted = false;
  isFutureHoldSubmitted = false;

  // Filter
  reserve: string | null = null
  reservePeople: number | null = null

  // Activity
  activity: Activity | null = null
  hasActiveVisit = false;
  hasActiveHold = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
              private datePipe: DatePipe,
              private router: Router,
              private route: ActivatedRoute,
              private routeService: RouteService,
              private authService: AuthService,
              private placeService: PlaceService,
              private regionService: RegionService,
              private productService: ProductService,
              private countdownService: CountdownService,
              private mapService: MapService,
              private roomService: RoomService,
              private holdService: HoldService,
              private visitService: VisitService,
              private activityService: ActivityService,
              private seoService: SeoService) {
  }

  ngOnInit() {
    this.authService.currentUser$.pipe(takeUntil(this.onDestroy$)).subscribe(currentUser => {
      if (currentUser) this.currentUser = currentUser;
    });

    // Context navigation based on /discover/region/neighborhood
    this.routeService.previousPath$.pipe(takeUntil(this.onDestroy$)).subscribe(previousPath => {
      if (previousPath && previousPath.startsWith("/discover")) {

        // Remove query parameters because it's already handled by router link (queryParamsHandling)
        if (previousPath.split("?").length > 1) {
          previousPath = previousPath.split("?")[0]
        }

        this.previousPath = previousPath;
      }
    });

    this.route.params.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
      this.regionSlug = params['regionSlug']
      this.neighborhoodSlug = params['neighborhoodSlug'];
      this.placeSlug = params['placeSlug'];

      this.refresh();
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  refresh() {
    if (!this.regionSlug) return
    if (!this.placeSlug) return

    this.loading = true;

    forkJoin([
      this.placeService.getBySlug(this.placeSlug),
      this.regionService.getBySlug(this.regionSlug)]).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.loading = false))
      .subscribe((results: any[]) => {
        this.place = results[0] as Place;
        this.region = results[1] as Region;

        this.place = this.enhancePlace(this.place);
        this.place.hoursOfOperation = this.calculateHoursOfOperation(this.place);

        const queryParamMap = this.route.snapshot.queryParamMap;
        // this.place.isPending = !!(!this.place.visible
        //   && queryParamMap.has('invite')
        //   && queryParamMap.get('invite').length > 0
        //   && queryParamMap.has('url')
        //   && queryParamMap.get('url').length > 0);

        this.refreshSeo();
        this.refreshGallery();
        this.refreshMap();
        this.refreshRooms();
        this.refreshReserveOptions();

        // this.refreshNearbyPlaces();

        if (this.currentUser) {
          this.refreshCountdown();
          this.refreshActivity();
        } else {
          this.calculateRemaining();
        }
      });
  }

  enhancePlace(place: Place) {
    if (place.localVerifiedHours
      && place.localVerifiedHours['sunday']!.allDay
      && place.localVerifiedHours['monday']!.allDay
      && place.localVerifiedHours['tuesday']!.allDay
      && place.localVerifiedHours['wednesday']!.allDay
      && place.localVerifiedHours['thursday']!.allDay
      && place.localVerifiedHours['friday']!.allDay
      && place.localVerifiedHours['saturday']!.allDay) {
      place.is247Verified = true;
    }
    return place;
  }

  refreshGallery() {
    if (!this.place) return;

    this.images = this.place.largeImages

    // Load photoswipe
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.gallery = new PhotoSwipeLightbox({
          gallery: '#gallery',
          children: 'a',
          pswpModule: () => import('photoswipe') // Dynamically load
        });
        this.gallery.init();
      }, 0)
    }
  }

  showGallery(index: number) {
    if (this.gallery) this.gallery.loadAndOpen(index);
  }

  calculateHoursOfOperation(place: Place) {
    const weekDay = moment().format('dddd');
    const day = weekDay.toLowerCase();

    // Verified hours
    const localVerifiedHoursMap: any = {
      "monday": place.localVerifiedHours!['monday'],
      "tuesday": place.localVerifiedHours!['tuesday'],
      "wednesday": place.localVerifiedHours!['wednesday'],
      "thursday": place.localVerifiedHours!['thursday'],
      "friday": place.localVerifiedHours!['friday'],
      "saturday": place.localVerifiedHours!['saturday'],
      "sunday": place.localVerifiedHours!['sunday'],
    }

    const isPlaceVerifiedHoursActive = place.localVerifiedHours && localVerifiedHoursMap[day].active
    const isPlaceVerifiedHoursAllDay = place.localVerifiedHours && localVerifiedHoursMap[day].allDay
    const isCurrentUserVerified = !!this.currentUser && !!this.currentUser.verification

    // Verified and open 24/7
    if (isCurrentUserVerified && isPlaceVerifiedHoursActive && isPlaceVerifiedHoursAllDay) {
      return 'Open 24 hours';
    }

    // Verified and open limited hours
    if (isCurrentUserVerified && isPlaceVerifiedHoursActive && !isPlaceVerifiedHoursAllDay) {
      return ('Today ' + this.datePipe.transform(localVerifiedHoursMap[day].begin, 'h:mm a') + ' - ' + this.datePipe.transform(localVerifiedHoursMap.end, 'h:mm a'));
    }

    // Non verified hours
    const localHoursMap: any = {
      "monday": place.localHours!['monday'],
      "tuesday": place.localHours!['tuesday'],
      "wednesday": place.localHours!['wednesday'],
      "thursday": place.localHours!['thursday'],
      "friday": place.localHours!['friday'],
      "saturday": place.localHours!['saturday'],
      "sunday": place.localHours!['sunday'],
    }

    const isPlaceHoursActive = place.localHours && localHoursMap[day].active
    const isPlaceHoursAllDay = place.localHours && localHoursMap[day].allDay

    // Not verified and open 24/7
    if (isPlaceHoursActive && isPlaceHoursAllDay) {
      return 'Open 24 hours';
    }

    // Not verified and open limited hours
    if (isPlaceHoursActive && !isPlaceHoursAllDay) {
      return ('Today ' + this.datePipe.transform(localHoursMap[day].begin, 'h:mm a') + ' - ' + this.datePipe.transform(localHoursMap[day].end, 'h:mm a'))
    }

    // Not open today
    if (!isPlaceHoursActive) {
      return `Closed ${weekDay}`;
    }

    // Default
    return "Closed"
  }

  toggleLike() {
    if (!this.currentUser) return;
    if (!this.place) return;

    const isLike = (this.place.likes!.indexOf(this.currentUser._id!) >= 0);
    if (isLike) {
      this.place.likes!.splice(this.place.likes!.indexOf(this.currentUser._id!), 1);
      this.placeService.unlike(this.place._id!).pipe(takeUntil(this.onDestroy$)).subscribe();
    } else {
      this.place.likes!.push(this.currentUser._id!);
      this.placeService.like(this.place._id!).pipe(takeUntil(this.onDestroy$)).subscribe();
    }
  }

  refreshCountdown() {
    if (!this.currentUser) return;
    if (!this.place) return;

    this.countdownService.getByPlace(this.place._id!)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((countdown: Countdown) => {
        this.countdown = {...countdown};
        this.calculateRemaining();
      });
  }

  refreshActivity() {
    if (!this.currentUser) return;

    const regionSlug = this.currentUser.region
    this.activityService.getByUser(regionSlug)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(activity => {
        this.activity = activity
        this.calculateActivity(activity);
      })
  }

  calculateActivity(activity: Activity) {
    this.hasActiveVisit = activity.activeVisits.length > 0
    this.hasActiveHold = activity.activeHolds.length > 0
  }

  refreshMap() {
    if (!this.place) return;

    const mapMark: MapMark = {
      latitude: this.place.location!.latitude!,
      longitude: this.place.location!.longitude!,
      place: this.place,
      isOpen: (this.place.isOpen! || (this.currentUser && this.currentUser.verification && this.place.isVerifiedOpen)) || false
    };

    this.mapService.setMapState(this.place.location!.latitude!, this.place.location!.longitude!, 16, [mapMark]);
  }

  refreshRooms() {
    if (!this.place) return;

    this.isRoomsLoading = true
    this.roomService.findByPlaceId(this.place._id!)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.isRoomsLoading = false
          this.isRoomsLoaded = true
        }))
      .subscribe(rooms => {
        this.rooms = rooms
      });
  }

  refreshReserveOptions() {
    // Query defaults
    const queryParamsReserve = this.route.snapshot.queryParams['reserve']
    const queryParamsReservePeople = this.route.snapshot.queryParams['reservePeople']

    if (queryParamsReserve) this.reserve = moment(queryParamsReserve).format('YYYY-MM-DDTHH:mm:ss')
    if (queryParamsReservePeople) this.reservePeople = queryParamsReservePeople
  }

  refreshSeo() {
    if (!this.place) return;
    if (!this.region) return;

    let title = `Croissant | ${this.place.name} Coworking Space | ${this.place.location!.neighborhood}, ${this.region.name}`
    let description = this.place.description

    this.seoService.setTitle(title)
    this.seoService.setDescription(description)
    if (this.place.smallImages.length > 0) this.seoService.setImage(this.place.smallImages[0].filePath)
  }

  calculateRemaining() {
    const product = this.countdown.membership?.product || null;

    const detail = this.productService.getProductDetail(product);
    if (detail.hasPassBasedMembership) {
      this.remaining = `${this.countdown.passRemaining}`;
      this.remainingUnit = 'visits';
    }

    if (detail.hasHourBasedMembership || detail.hasFlexBasedMembership) {
      const duration = moment.duration(Math.max(this.countdown.visitRemaining!, 0));

      this.remaining = duration.asHours() < 1 ? round(duration.asMinutes()).toString() : round(duration.asHours()).toString();
      this.remainingUnit = duration.asHours() < 1 ? 'minutes' : 'hours';
    }
  }

  createVisit() {
    if (!this.currentUser) {
      this.router.navigate(['/login'], {queryParams: {redirect: location.pathname}}).finally();
      return;
    }

    if (!this.place) return
    if (this.isVisitSubmitted) return
    if (this.isHoldSubmitted) return
    if (this.isFutureHoldSubmitted) return

    this.isVisitSubmitted = true
    this.visitService.createVisit(this.place._id).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.isVisitSubmitted = false))
      .subscribe(() => {
        this.router.navigate(['/activity']).finally();
      });
  }

  createHold() {
    if (!this.currentUser) {
      this.router.navigate(['/login'], {queryParams: {redirect: location.pathname}, queryParamsHandling: "preserve"}).finally();
      return;
    }

    if (!this.place) return
    if (this.isVisitSubmitted) return
    if (this.isHoldSubmitted) return
    if (this.isFutureHoldSubmitted) return

    this.isHoldSubmitted = true;
    this.holdService.createHold(this.place._id).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.isHoldSubmitted = false))
      .subscribe(() => {
        this.router.navigate(['/activity']).finally();
      });
  }

  createFutureHold() {
    if (!this.currentUser) {
      this.router.navigate(['/login'], {queryParams: {redirect: location.pathname + location.search}}).finally();
      return;
    }

    if (!this.place) return
    if (!this.reserve) return
    if (this.isVisitSubmitted) return
    if (this.isHoldSubmitted) return
    if (this.isFutureHoldSubmitted) return

    const placeId = this.place._id
    const begin = moment(this.reserve).format('YYYY-MM-DDTHH:mm:ss');
    const end = null // No implementing

    this.holdService.createFutureHold(placeId, begin, end).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.isHoldSubmitted = false))
      .subscribe(() => {
        this.router.navigate(['/activity']).finally();
      });
  }

}
